import { Button } from '@design-system/components/button/button';
import { IconButton } from '@design-system/components/button/iconButton';
import { PlainModal } from '@design-system/components/modal/modal';
import { Typography } from '@design-system/components/typography/typography';
import {
  faCalendar,
  faCalendarClock,
} from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { MenuItem, Select } from '@mui/material';
import moment from 'moment-timezone';
import { EditWorkoutVM } from '../view-models/workoutEdit.viewModel';
import { toOption, toOptionsConstructor } from '@client/new/helpers/types';

export const timezoneValues = [
  'America/New_York',
  'America/Los_Angeles',
  'Europe/Berlin',
  'Europe/London',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
] as const;

export type Timezone = (typeof timezoneValues)[number];

const DEFAULT_TIMEZONE = timezoneValues[0];

// The modal only lets the user pick date/hour/minute, so seconds inherited
// from the moment the modal opened must not leak into the saved timestamp.
const toIsoString = (value: moment.Moment) =>
  value.clone().seconds(0).milliseconds(0).toISOString();

const changeHour = (value: moment.Moment, hour: number) =>
  value.clone().hour(value.hour() >= 12 ? (hour % 12) + 12 : hour % 12);

const changeMinutes = (value: moment.Moment, minutes: number) =>
  value.clone().minute(minutes);

const changeAmPm = (value: moment.Moment, nextAmPm: string) => {
  const isPm = value.hour() >= 12;
  const wantsPm = nextAmPm === 'PM';

  if (isPm === wantsPm) {
    return value.clone();
  }

  return value.clone().add(isPm ? -12 : 12, 'hours');
};

const changeDate = (value: moment.Moment, date: Date) => {
  const nextDate = moment(date);

  return value
    .clone()
    .year(nextDate.year())
    .month(nextDate.month())
    .date(nextDate.date());
};

export const Schedule = ({
  dispatch,
  watch,
  disabled,
}: Pick<EditWorkoutVM, 'dispatch' | 'watch'> & { disabled?: boolean }) => {
  const [open, setOpen] = useState(false);
  const timezone = watch('timeZone');
  const publishedAt = watch('publishedAt');
  const selectedTz = timezone || DEFAULT_TIMEZONE;

  const publishedMoment = moment.tz(publishedAt, selectedTz);
  const publishedDate = publishedMoment.format('L');

  const ampm = publishedMoment.format('A');
  const hours =
    ampm === 'PM' ? publishedMoment.hours() - 12 : publishedMoment.hours();
  const minutes = publishedMoment.minutes();

  const onChangeMinutes = (min: number) => {
    dispatch(
      'setPublishedAt',
      toIsoString(changeMinutes(publishedMoment, min)),
    );
  };

  const onChangeHours = (hour: number) => {
    dispatch('setPublishedAt', toIsoString(changeHour(publishedMoment, hour)));
  };

  const onChangeAmPm = (value: string) => {
    dispatch('setPublishedAt', toIsoString(changeAmPm(publishedMoment, value)));
  };

  const changeTimeZone = (newTz: string) => {
    if (publishedAt) {
      const recalculated = moment.tz(publishedAt, selectedTz).tz(newTz, true);
      dispatch('setPublishedAt', toIsoString(recalculated));
    }
    dispatch('setTimeZone', newTz);
  };

  const onChangeDate = (date: Date | null) => {
    if (date !== null) {
      dispatch(
        'setPublishedAt',
        toIsoString(changeDate(publishedMoment, date)),
      );
    }
  };

  const openModal = () => {
    if (!timezone) {
      dispatch('setTimeZone', DEFAULT_TIMEZONE);
    }
    setOpen(true);
  };

  const schedule = () => {
    setOpen(false);
    dispatch('publish');
  };

  const cancel = () => {
    setOpen(false);
    dispatch('clearSchedule');
  };

  return (
    <>
      <Buttons
        publishedDate={publishedDate}
        setOpen={openModal}
        isDateSet={!!publishedAt}
        disabled={disabled}
      />
      <PlainModal
        isOpen={open}
        onClose={() => setOpen(false)}
        modalClassName="max-w-[362px]"
      >
        <div className="flex flex-col gap-6">
          <Typography>
            Select date & time when your workout will be published
          </Typography>
          <div className="flex justify-center">
            <DatePicker
              inline
              className="w-[414px]"
              value={publishedAt}
              onChange={onChangeDate}
              minDate={new Date()}
            />
          </div>
          <div className="flex flex-col gap-4">
            <Timezone value={selectedTz} onChange={changeTimeZone} />
            <div className="flex justify-between">
              <Hour value={Number(hours)} onChange={onChangeHours} />
              <div className="flex items-center gap-2">
                <Typography>:</Typography>
                <Minutes value={Number(minutes)} onChange={onChangeMinutes} />
              </div>
              <DayTime value={ampm} onChange={onChangeAmPm} />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="text" color="neutral" onClick={cancel}>
              Cancel
            </Button>
            <Button color="neutral" onClick={schedule}>
              Schedule
            </Button>
          </div>
        </div>
      </PlainModal>
    </>
  );
};

const Buttons = ({
  publishedDate,
  setOpen,
  isDateSet,
  disabled,
}: {
  publishedDate: string;
  setOpen: () => void;
  isDateSet: boolean;
  disabled?: boolean;
}) => {
  if (isDateSet) {
    return (
      <Button
        variant="text"
        leftIcon={faCalendar}
        onClick={setOpen}
        testId="scheduleWorkoutDate"
        disabled={disabled}
      >
        {publishedDate}
      </Button>
    );
  }
  return (
    <IconButton
      color="neutral"
      icon={faCalendarClock}
      onClick={setOpen}
      data-testid="scheduleWorkout"
      disabled={disabled}
    />
  );
};

const tz = toOptionsConstructor<Timezone>();

export const timezoneOptions = [
  tz('America/New_York', 'New York (EST)'),
  tz('America/Los_Angeles', 'San Francisco (PST)'),
  tz('Europe/Berlin', 'Berlin (CET)'),
  tz('Europe/London', 'London (GMT)'),
  tz('Asia/Shanghai', 'Shanghai (CST)'),
  tz('Asia/Tokyo', 'Tokyo (JST)'),
  tz('Australia/Sydney', 'Sydney (AEDT)'),
];

export const Timezone = (p: {
  value?: string;
  onChange: (value: string) => void;
}) => (
  <Select className="w-[184px]" value={p.value}>
    {timezoneOptions.map(({ label, value }) => (
      <MenuItem key={label} value={value} onClick={() => p.onChange(value)}>
        <Typography variant="body" size="large">
          {label}
        </Typography>
      </MenuItem>
    ))}
  </Select>
);

const leadingZeroFormatter = new Intl.NumberFormat(undefined, {
  minimumIntegerDigits: 2,
});

export const Hour = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) => (
  <Select className="w-[83px]" value={value}>
    {[...Array(12).keys()].map((v) => (
      <MenuItem key={v} value={v} onClick={() => onChange(v)}>
        <Typography variant="body" size="large">
          {leadingZeroFormatter.format(v)}
        </Typography>
      </MenuItem>
    ))}
  </Select>
);

export const Minutes = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) => (
  <Select className="w-[83px]" value={value}>
    {[...Array(59).keys()].map((v) => (
      <MenuItem key={v} value={v} onClick={() => onChange(v)}>
        <Typography variant="body" size="large">
          {leadingZeroFormatter.format(v)}
        </Typography>
      </MenuItem>
    ))}
  </Select>
);

export const dayTimeValues = ['AM', 'PM'] as const;
export type DayTime = (typeof dayTimeValues)[number];
export const dayTimeOptions = [
  toOption<DayTime>('AM', 'AM'),
  toOption<DayTime>('PM', 'PM'),
];

export const DayTime = (p: {
  value: string | undefined;
  onChange: (value: string) => void;
}) => (
  <Select className="w-[83px]" value={p.value}>
    {dayTimeOptions.map(({ label, value }) => (
      <MenuItem key={label} value={value} onClick={() => p.onChange(value)}>
        <Typography variant="body" size="large">
          {label}
        </Typography>
      </MenuItem>
    ))}
  </Select>
);
